<!-- Modal -->
<template>
    <div class="modal fade" id="popupModal" ref="popupModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header tw-red-bg-color text-white">
                    <div class="d-flex">
                        <span class="tw-create-modal-icon d-inline-flex justify-content-center align-items-center tw-light-red-bg-color me-3">
                            <i class="fas fa-bullhorn"></i>
                        </span>
                        <span class="d-inline-flex">
                            <div class="row">
                                <h5 class="modal-title col-12">Homepage Popup</h5>
                                <p class="col-12 mb-0">Small announcement box shown on your website homepage</p>
                            </div>
                        </span>
                    </div>
                </div>

                <form @submit.prevent.stop="save">
                    <div class="modal-body">
                        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

                        <div class="row">
                            <div class="col-xl-6">
                                <div class="form-group mb-4">
                                    <label class="mb-2">Status</label>
                                    <label class="tw-popup-toggle d-flex align-items-center cursor-pointer mb-0">
                                        <input type="checkbox" class="d-none" v-model="popup.active">
                                        <span class="tw-popup-toggle-track me-3" :class="{ 'is-on': popup.active }"><span class="tw-popup-toggle-thumb"></span></span>
                                        <span>{{ popup.active ? 'Active' : 'Inactive' }} <small class="tw-grey-color">{{ popup.active ? '(shown on homepage)' : '(hidden from homepage)' }}</small></span>
                                    </label>
                                </div>
                            </div>
                            <div class="col-xl-6">
                                <div class="form-group mb-4">
                                    <label for="popup-position" class="mb-2">Position</label>
                                    <select class="form-control" id="popup-position" v-model="popup.position">
                                        <option value="bottom-right">Bottom right</option>
                                        <option value="bottom-left">Bottom left</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-xl-6">
                                <div class="form-group mb-4">
                                    <label for="popup-title" class="mb-2">Title</label>
                                    <input type="text" class="form-control" id="popup-title" v-model="popup.title" maxlength="150" :required="popup.active" placeholder="Title of the popup">
                                </div>
                            </div>
                            <div class="col-xl-6">
                                <div class="form-group mb-4">
                                    <label for="popup-type" class="mb-2 d-flex justify-content-between">
                                        <span>Type <small class="fw-normal"><i>(Optional)</i></small></span>
                                        <small class="fw-normal tw-grey-color">{{ (popup.type || '').length }}/50</small>
                                    </label>
                                    <input type="text" class="form-control" id="popup-type" v-model="popup.type" maxlength="50" placeholder="e.g. Upcoming">
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="row mb-2">
                                <label class="col-md-6 d-flex align-items-center">Content</label>
                            </div>
                            <editor v-if="editorReady" api-key="" v-model="popup.content" :init="tinymceInit" />
                            <div v-else class="tw-popup-editor-placeholder"></div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button class="btn tw-red-bg-color text-white" :disabled="spinner || !userHasPermission('create_posts')">
                            Save
                            <span v-if="spinner"><i class="fas fa-spinner fa-spin"></i></span>
                        </button>
                        <button class="btn tw-dark-grey-bg-color text-white" type="button" @click="modal.hide()">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
<script>
    import { Modal } from 'bootstrap';
    import editor from '@tinymce/tinymce-vue';
    import cms6Mixin from '@/mixins/cms6Mixin';

    // Plain (non-reactive) object — TinyMCE gets its own copy of the settings
    const tinymceInit = {
        height: 300,
        // main.css pins .tox-editor-header over the bottom of the edit area — same
        // bottom spacing as the shared news/pages config so text can scroll clear of it
        content_style: 'body#tinymce { margin-bottom: 50px; }',
        menubar: false,
        contextmenu: false,
        plugins: ['autolink lists link paste'],
        toolbar: 'bold italic underline | bullist numlist | link | removeformat',
        default_link_target: '_blank',
    };

    const emptyPopup = () => ({ active: false, position: 'bottom-right', type: '', title: '', content: '' });

    export default {
        name: 'PopupModal',
        components: {
            editor,
        },
        emits: ['popupSaved'],
        data: () => ({
            modal: null,
            popup: emptyPopup(),
            spinner: false,
            error: '',
            // Editor is mounted on first open (TinyMCE initialized inside the hidden
            // modal on page load never shows) and then kept — unmounting and
            // re-initializing it on every open fails every other time.
            editorReady: false,
        }),
        mounted() {
            this.modal = new Modal(this.$refs.popupModal);
            this.$refs.popupModal.addEventListener('shown.bs.modal', () => { this.editorReady = true; });

            // Prevent Bootstrap dialog from blocking focusin (TinyMCE link dialog)
            document.addEventListener('focusin', (e) => {
                if (e.target.closest('.tox-tinymce-aux') !== null) {
                    e.stopImmediatePropagation();
                }
            });
        },
        computed: {
            tinymceInit: () => tinymceInit,
        },
        methods: {
            showModal(popup) {
                this.popup = { ...emptyPopup(), ...(popup || {}) };
                this.error = '';
                this.modal.show();
            },
            async save() {
                this.error = '';
                if (this.popup.active && !this.stripHtml(this.popup.content).trim()) {
                    this.error = 'Content is required for an active popup.';
                    return;
                }

                this.spinner = true;
                try {
                    let res = await this.savePopup(this.popup);
                    this.modal.hide();
                    this.$emit('popupSaved', res.data.data);
                    this.$swal({
                        title: 'Popup saved!',
                        text: 'Your homepage is being updated.',
                        toast: true,
                        position: 'bottom-end',
                        showCloseButton: true,
                        timer: 5000,
                        timerProgressBar: true,
                        showConfirmButton: false,
                        icon: 'success',
                    });
                } catch (err) {
                    let errors = err.response && err.response.data && err.response.data.errors;
                    this.error = errors ? Object.values(errors).flat().join(' ') : 'Popup wasn\'t saved.';
                } finally {
                    this.spinner = false;
                }
            },
            stripHtml(html) {
                let div = document.createElement('div');
                div.innerHTML = html || '';
                return div.textContent || '';
            },
        },
        mixins: [cms6Mixin],
    }
</script>
<style scoped>
    .tw-popup-toggle {
        height: 37px;
        user-select: none;
    }
    .tw-popup-toggle-track {
        position: relative;
        display: inline-block;
        flex-shrink: 0;
        width: 46px;
        height: 26px;
        border-radius: 13px;
        background: #ccd1d8;
        transition: background .2s;
    }
    .tw-popup-toggle-track.is-on {
        background: var(--tw-red);
    }
    .tw-popup-toggle-thumb {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
        transition: transform .2s;
    }
    .tw-popup-toggle-track.is-on .tw-popup-toggle-thumb {
        transform: translateX(20px);
    }
    .tw-popup-editor-placeholder {
        height: 300px;
        border: 1px solid #ccc;
        border-radius: 15px;
    }
    @media only screen and (min-width: 992px) {
        .modal-lg {
            max-width: 900px;
        }
    }
</style>
